import { useEffect, useCallback, useMemo } from "react";
import { register, Hanko } from "@teamhanko/hanko-elements";
import { all } from "@teamhanko/hanko-elements/i18n/all";
import { fiCustom } from "../i18n/login/fiCustom";
import { svCustom } from "../i18n/login/svCustom";
import { enCustom } from "../i18n/login/enCustom";
import {config} from "../config";
import { consumePostLoginRedirectTarget } from "../utils/authRedirect";
import { queuePendingAuthTrackingEvent } from "../analytics/authTrackingQueue";
import { trackAppEvent, trackAppEventOnce } from "../analytics/trackEvent";

const hankoApi = config.hankoUrl;

// Module-level flag: prevents duplicate redirects when multiple HankoAuth
// instances have overlapping onSessionCreated listeners (e.g. /login listener
// surviving a client-side navigation to /register).
let redirecting = false;

const HANKO_AUTOMATIC_ACTIONS = new Set(['register_client_capabilities']);
const SIGNUP_START_ACTIONS = new Set(['register_login_identifier', 'thirdparty_oauth']);
const AUTH_STEP_BY_STATE = {
    registration_init: 'email',
    passcode_confirmation: 'email_verification',
    password_creation: 'password_creation',
    credential_onboarding_chooser: 'credential_choice',
    onboarding_create_passkey: 'passkey_setup',
    onboarding_verify_passkey_attestation: 'passkey_verification',
    webauthn_credential_verification: 'passkey_verification',
    mfa_method_chooser: 'mfa_choice',
    mfa_otp_secret_creation: 'mfa_authenticator_setup',
    mfa_security_key_creation: 'mfa_security_key_setup',
    device_trust: 'device_trust',
    thirdparty: 'oauth_redirect'
};

const getAuthJourney = (mode) => {
    const hasPendingInvite = Boolean(sessionStorage.getItem('pendingInviteToken'));
    if (hasPendingInvite) return 'invite';
    return mode === 'registration' ? 'new_org' : 'login';
};

const getAuthEventPrefix = (journey) => {
    if (journey === 'new_org') return 'signup_auth';
    if (journey === 'invite') return 'invite_auth';
    return null;
};

const getSafeAuthProperties = (mode, journey, state, action) => ({
    auth_mode: mode,
    auth_journey: journey,
    auth_step: AUTH_STEP_BY_STATE[state?.name] || state?.name,
    auth_state: state?.name,
    auth_action: action,
    auth_http_status: state?.status,
    signup_type: journey === 'new_org' ? 'new_org' : journey === 'invite' ? 'invite' : undefined,
    signup_event_version: journey === 'login' ? undefined : 'v2'
});

const getAuthErrorCodes = (state) => {
    const errorCodes = new Set();
    if (state?.error?.code) errorCodes.add(state.error.code);

    Object.values(state?.actions || {}).forEach((action) => {
        Object.values(action?.inputs || {}).forEach((input) => {
            if (input?.error?.code) errorCodes.add(input.error.code);
        });
    });

    return [...errorCodes];
};

const mergeHankoTranslations = (base = {}, custom = {}) => ({
    ...base,
    ...custom,
    headlines: { ...(base.headlines || {}), ...(custom.headlines || {}) },
    texts: { ...(base.texts || {}), ...(custom.texts || {}) },
    labels: { ...(base.labels || {}), ...(custom.labels || {}) },
    errors: { ...(base.errors || {}), ...(custom.errors || {}) },
    flowErrors: { ...(base.flowErrors || {}), ...(custom.flowErrors || {}) },
});

export default function HankoAuth({ language, mode }) {
    const hanko = useMemo(() => new Hanko(hankoApi, { language }), [language]);

    const redirectAfterLogin = useCallback(() => {
        if (redirecting) return;
        redirecting = true;
        const pendingToken = sessionStorage.getItem('pendingInviteToken');
        const authJourney = pendingToken ? 'invite' : mode === 'registration' ? 'new_org' : 'login';
        const completedEvent = authJourney === 'invite'
            ? 'invite_auth_completed'
            : mode === 'registration'
                ? 'signup_completed'
                : 'login_completed';

        queuePendingAuthTrackingEvent(completedEvent, {
            auth_mode: mode,
            auth_journey: authJourney,
            signup_type: authJourney === 'new_org' ? 'new_org' : authJourney === 'invite' ? 'invite' : undefined,
            signup_event_version: authJourney === 'login' ? undefined : 'v2'
        });

        if (pendingToken) {
            sessionStorage.removeItem('pendingInviteToken');
            window.location.assign(`/accept-invite?token=${encodeURIComponent(pendingToken)}`);
        } else {
            const redirectTarget = consumePostLoginRedirectTarget() || "/";
            window.location.assign(redirectTarget);
        }
    }, [mode]);

    useEffect(() => {
        const unsubscribe = hanko?.onSessionCreated(() => {
            redirectAfterLogin();
        });
        return () => {
            if (typeof unsubscribe === 'function') unsubscribe();
        };
    }, [hanko, redirectAfterLogin]);

    useEffect(() => {
        const handleBeforeStateChange = ({ state } = {}) => {
            if (!state || state.flowName !== mode) return;

            const action = state.invokedAction?.name;
            if (!action || HANKO_AUTOMATIC_ACTIONS.has(action)) return;

            const journey = getAuthJourney(mode);
            const eventPrefix = getAuthEventPrefix(journey);
            if (!eventPrefix) return;

            const properties = getSafeAuthProperties(mode, journey, state, action);
            if (journey === 'new_org' && state.name === 'registration_init' && SIGNUP_START_ACTIONS.has(action)) {
                trackAppEventOnce('registration-signup-started:new_org', 'signup_started', {
                    ...properties,
                    signup_trigger: action
                });
            } else if (journey === 'invite') {
                trackAppEventOnce('invite-auth-started', 'invite_auth_started', properties);
            }

            trackAppEvent(`${eventPrefix}_step_submitted`, properties);
        };

        const handleAfterStateChange = ({ state } = {}) => {
            if (!state || state.flowName !== mode || state.name === 'preflight') return;

            const journey = getAuthJourney(mode);
            const eventPrefix = getAuthEventPrefix(journey);
            if (!eventPrefix) return;

            const previousAction = state.previousAction?.name;
            const properties = getSafeAuthProperties(mode, journey, state, previousAction);

            if (state.name !== 'success') {
                trackAppEventOnce(
                    `${eventPrefix}-step-viewed:${state.name}`,
                    `${eventPrefix}_step_viewed`,
                    properties
                );
            }

            getAuthErrorCodes(state).forEach((errorCode) => {
                trackAppEvent(`${eventPrefix}_error`, {
                    ...properties,
                    auth_error_code: errorCode
                });
            });
        };

        const unsubscribeBefore = hanko?.onBeforeStateChange(handleBeforeStateChange);
        const unsubscribeAfter = hanko?.onAfterStateChange(handleAfterStateChange);

        return () => {
            if (typeof unsubscribeBefore === 'function') unsubscribeBefore();
            if (typeof unsubscribeAfter === 'function') unsubscribeAfter();
        };
    }, [hanko, mode]);

    useEffect(() => {
        register(hankoApi, {
            translations: {
                ...all,
                en: mergeHankoTranslations(all.en, enCustom),
                fi: mergeHankoTranslations(all.fi, fiCustom),
                sv: mergeHankoTranslations(all.sv, svCustom),
            }
        }).catch((error) => {
            console.error("Hanko registration failed:", error);
            const journey = getAuthJourney(mode);
            const eventPrefix = getAuthEventPrefix(journey);
            if (eventPrefix) {
                trackAppEvent(`${eventPrefix}_error`, {
                    auth_mode: mode,
                    auth_journey: journey,
                    auth_error_code: 'hanko_component_registration_failed',
                    signup_type: journey === 'new_org' ? 'new_org' : journey === 'invite' ? 'invite' : undefined,
                    signup_event_version: journey === 'login' ? undefined : 'v2'
                });
            }
        });
    }, [mode]);

    if (mode === "registration") {
        return <hanko-registration lang={language} />;
    }
    return <hanko-login lang={language} />;
}
